import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button, Box, Container,
    Accordion, AccordionSummary, AccordionDetails,
    Typography, Paper, CircularProgress
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Layout from '../../common/Layout';
import LoadingSpinner from '../../common/components/LoadingSpinner/LoadingSpinner';
import ChallengeResultSteps from '../../common/components/ChallengeResultSteps/ChallengeResultSteps';
import { useUserChallenges } from '../../hooks/useUserChallenges';
import { useResultsChallenge } from '../../hooks/useResultsChallenge';

const useStyles = makeStyles((theme) => ({
    goBackBtn: {
        marginBottom: 10,
        [theme.breakpoints.down('xs')]: {
            marginBottom: 35,
            fontSize: theme.typography.pxToRem(12),
        },
        '&:hover': {
            color: theme.palette.text.primary,
        },
        '&:focus': {
            outline: 'none',
        },
    },
    container: {
        padding: 0,
    },
    topicBlock: {
        padding: theme.spacing(3),
        paddingBottom: 34,
        marginTop: 24,
        borderRadius: 6
    },
    topicTitle: {
        fontSize: theme.typography.pxToRem(21),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightMedium,
        marginBottom: 20,
    },
    accordion: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: '1px solid #ff9800',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: '#F6F8FB',
        boxShadow: '0 2px 4px rgba(255, 152, 0, 0.2)',
        '&:before': {
            display: 'none'
        }
    },
    accordionSummary: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    challengeTitle: {
        fontSize: theme.typography.pxToRem(20)
    },
}));

const groupByLearningTopic = (records) => {
    return records.reduce((acc, record) => {
        const topic = record.challengeLearningTopic || 'Other';
        if (!acc[topic]) acc[topic] = [];
        acc[topic].push(record);
        return acc;
    }, {});
};

const ChallengeAccordion = ({ challenge, classes }) => {
    const [expanded, setExpanded] = useState(false);

    const {
        data,
        isFetching: loading,
        error
    } = useResultsChallenge(challenge._id, expanded);

    const { stepResults, currentStep, status, blockedSteps } = data?.challengeResults || {};

    return (
        <Accordion
            className={classes.accordion}
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
            >
                <Typography className={classes.challengeTitle}>{challenge.challengeTitle}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box width="100%">
                    {loading && (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={28} style={{ color: '#ff9800' }} />
                        </Box>
                    )}
                    {error && (
                        <Typography color="error" variant="body2">
                            Error loading results: {error.message}
                        </Typography>
                    )}
                    {!loading && !error && stepResults && (
                        <ChallengeResultSteps
                            stepResults={stepResults}
                            challengeId={challenge._id}
                            currentStep={currentStep}
                            status={status}
                            blockedSteps={blockedSteps}
                        />
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const StudentSchedule = () => {
    const classes = useStyles();
    const { id } = useParams();
    const navigate = useNavigate();
    const userEmail = id ? decodeURIComponent(id) : null;

    const {
        data: userChallengesData,
        isFetching: userChallengesLoading,
        error: userChallengesError,
    } = useUserChallenges(
        userEmail,
        { enabled: !!userEmail }
    );

    if (userChallengesLoading) {
        return (
            <Layout>
                <LoadingSpinner styles={{ paddingTop: 60 }} />
            </Layout>
        );
    }

    if (userChallengesError) {
        return (
            <Layout>
                <div>Error! {userChallengesError.message}</div>
            </Layout>
        );
    }

    const records = userChallengesData?.userChallenges?.records || [];
    const grouped = groupByLearningTopic(records);

    return (
        <Layout>
            <Box component="div" display="flex" justifyContent="space-between">
                
            </Box>
            <Container className={classes.container} maxWidth="xl" disableGutters>
                {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([topic, challenges]) => (
                    <Paper key={topic} className={classes.topicBlock} elevation={2}>
                        <Typography className={classes.topicTitle} variant="h3">
                            {topic}
                        </Typography>
                        {challenges.map((challenge) => (
                            <ChallengeAccordion
                                key={challenge._id}
                                challenge={challenge}
                                classes={classes}
                            />
                        ))}
                    </Paper>
                ))}
            </Container>
        </Layout>
    );
};

export default StudentSchedule;
